import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { API as API_BASE } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { NUTRIENT_GROUPS, formatDateTime, formatNum, formatKg } from "../lib/nutrition";
import { NutriScoreBadge } from "../components/NutriScoreBadge";
import {
  ArrowLeft, FloppyDisk, Plus, Trash, FilePdf, FileXls, MagnifyingGlass,
  ClockCounterClockwise, Copy, ArrowCounterClockwise, BookOpenText, Tag,
} from "@phosphor-icons/react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

// Generiert eine schwach-eindeutige UID für Item-Stable-Keys in React-Listen
let _itemUidCounter = 0;
const newItemUid = () => `it_${Date.now()}_${++_itemUidCounter}`;

/** Erkennt Trinkwasser-/Wasser-Zutaten, die standardmäßig in Litern eingegeben werden sollen. */
function isWaterIngredient(ing) {
  if (!ing) return false;
  const n = String(ing.name || "").toLowerCase();
  return n === "trinkwasser" || n === "wasser" || n.startsWith("wasser ") || n.startsWith("trinkwasser ");
}

async function downloadFile(url, filename) {
  const token = localStorage.getItem("nc_token");
  const res = await fetch(url, {
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error("Export fehlgeschlagen");
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

export default function RecipeBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const isEdit = id && id !== "neu";

  const [meta, setMeta] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [recipesList, setRecipesList] = useState([]); // andere Rezepte (für Sub-Komponenten)
  const [allergenMeta, setAllergenMeta] = useState([]);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [portions, setPortions] = useState(1);
  const [items, setItems] = useState([]); // {ingredient_id, ref_type, amount, unit}
  const [gtin, setGtin] = useState("");
  const [artikelnummer, setArtikelnummer] = useState("");
  const [declareAsCooked, setDeclareAsCooked] = useState(false);
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [savedId, setSavedId] = useState(isEdit ? id : null);
  const [currentVersion, setCurrentVersion] = useState(1);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [changeReason, setChangeReason] = useState("");
  const [compareTarget, setCompareTarget] = useState(null); // a version object

  // Live computed totals
  const [computed, setComputed] = useState({ total_grams: 0, totals: {}, per_100g: {}, per_portion: {}, allergens: [], alcohol_g: 0, alcohol_g_per_100g: 0, contains_alcohol: false, nutri_score: null });

  const metaByKey = useMemo(() => {
    const m = {};
    meta.forEach((x) => { m[x.key] = x; });
    return m;
  }, [meta]);

  const allergenLabel = useMemo(() => {
    const m = {};
    allergenMeta.forEach((x) => { m[x.key] = x.label; });
    return m;
  }, [allergenMeta]);

  useEffect(() => {
    (async () => {
      const [a, b, c, r] = await Promise.all([
        api.get("/meta/nutrients"),
        api.get("/ingredients"),
        api.get("/meta/allergens"),
        api.get("/recipes"),
      ]);
      setMeta(a.data);
      setIngredients(b.data);
      setAllergenMeta(c.data);
      setRecipesList(r.data || []);
      if (isEdit) {
        try {
          const { data } = await api.get(`/recipes/${id}`);
          setName(data.name);
          setDescription(data.description || "");
          setGtin(data.gtin || "");
          setArtikelnummer(data.artikelnummer || "");
          setDeclareAsCooked(Boolean(data.declare_as_cooked));
          setPortions(data.portions || 1);
          setItems((data.items || []).map((it) => ({
            _uid: newItemUid(),
            ingredient_id: it.ingredient_id,
            ref_type: it.ref_type || "ingredient",
            amount: it.amount != null ? it.amount : it.grams,
            unit: it.unit || "g",
          })));
          setComputed({
            total_grams: data.total_grams,
            totals: data.totals,
            per_100g: data.per_100g,
            per_portion: data.per_portion,
            allergens: data.allergens || [],
            alcohol_g: data.alcohol_g || 0,
            alcohol_g_per_100g: data.alcohol_g_per_100g || 0,
            contains_alcohol: Boolean(data.contains_alcohol),
            nutri_score: data.nutri_score || null,
          });
          setAudit({
            created_by_name: data.created_by_name, created_at: data.created_at,
            updated_by_name: data.updated_by_name, updated_at: data.updated_at,
          });
          setCurrentVersion(data.version || 1);
        } catch {
          toast.error("Rezept nicht gefunden");
          nav("/rezepte");
        }
      }
    })();
  }, [id, isEdit, nav]);

  // Live calc on changes
  const recalc = useCallback(async () => {
    try {
      const { data } = await api.post("/recipes/calculate", {
        name: name || "tmp", portions: Number(portions) || 1, items,
        declare_as_cooked: declareAsCooked,
      });
      setComputed({ ...data, allergens: data.allergens || [] });
    } catch (err) {
      console.error("Live-Berechnung fehlgeschlagen:", err);
    }
  }, [items, portions, name, declareAsCooked]);

  useEffect(() => {
    const t = setTimeout(recalc, 200);
    return () => clearTimeout(t);
  }, [recalc]);

  const ingById = useMemo(() => {
    const m = {};
    ingredients.forEach((i) => { m[i.id] = i; });
    return m;
  }, [ingredients]);

  const addItem = () => {
    if (ingredients.length === 0) {
      toast.error("Zuerst Zutaten anlegen");
      return;
    }
    const first = ingredients[0];
    // Trinkwasser standardmäßig in Litern, sonst Gramm
    const defaultUnit = isWaterIngredient(first) && first.density_g_per_ml ? "l" : "g";
    const defaultAmount = defaultUnit === "l" ? 1 : 100;
    setItems((s) => [...s, { _uid: newItemUid(), ingredient_id: first.id, ref_type: "ingredient", amount: defaultAmount, unit: defaultUnit }]);
  };

  const addRecipeComponent = () => {
    const otherRecipes = recipesList.filter((r) => r.id !== savedId);
    if (otherRecipes.length === 0) {
      toast.error("Keine anderen Rezepte vorhanden, die du als Komponente einfügen kannst.");
      return;
    }
    setItems((s) => [...s, { _uid: newItemUid(), ingredient_id: otherRecipes[0].id, ref_type: "recipe", amount: 100, unit: "g" }]);
  };

  const updateItem = (idx, patch) =>
    setItems((s) => s.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const removeItem = (idx) =>
    setItems((s) => s.filter((_, i) => i !== idx));

  // Effektive Gramm einer einzelnen Zeile (Frontend-Vorschau)
  const itemGrams = (it) => {
    const amount = Number(it.amount) || 0;
    const unit = it.unit || "g";
    if (it.ref_type === "recipe") {
      // Sub-Rezepte unterstützen g/kg
      if (unit === "kg") return amount * 1000;
      return amount;
    }
    const ing = ingById[it.ingredient_id];
    if (unit === "g") return amount;
    if (unit === "kg") return amount * 1000;
    if (unit === "ml") return amount * (Number(ing?.density_g_per_ml) || 1);
    if (unit === "l")  return amount * 1000 * (Number(ing?.density_g_per_ml) || 1);
    if (unit === "piece") return amount * (Number(ing?.piece_weight_g) || 0);
    return amount;
  };

  const save = async () => {
    if (!name.trim()) {
      toast.error("Bitte einen Rezeptnamen angeben");
      return;
    }
    // Beim Bearbeiten optionalen Änderungsgrund abfragen
    if (isEdit) {
      setChangeReason("");
      setReasonOpen(true);
      return;
    }
    await doSave("");
  };

  const doSave = async (reason) => {
    setBusy(true);
    try {
      const body = {
        name, description, portions: Number(portions) || 1,
        reference_amount_g: null,
        gtin: gtin.trim() || null,
        artikelnummer: artikelnummer.trim() || null,
        declare_as_cooked: declareAsCooked,
        items: items.map((it) => ({
          ingredient_id: it.ingredient_id,
          ref_type: it.ref_type || "ingredient",
          amount: Number(it.amount) || 0,
          unit: it.unit || "g",
        })),
      };
      if (isEdit && reason) body.change_reason = reason;
      const res = isEdit
        ? await api.put(`/recipes/${id}`, body)
        : await api.post("/recipes", body);
      setSavedId(res.data.id);
      setCurrentVersion(res.data.version || 1);
      toast.success(isEdit ? "Rezept aktualisiert" : "Rezept erstellt");
      if (!isEdit) nav(`/rezepte/${res.data.id}`);
    } catch {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setBusy(false);
      setReasonOpen(false);
    }
  };

  const loadVersions = async () => {
    if (!savedId) return;
    try {
      const { data } = await api.get(`/recipes/${savedId}/versions`);
      setVersions(data.versions || []);
      setCurrentVersion(data.current_version || 1);
      setVersionsOpen(true);
    } catch {
      toast.error("Versionen konnten nicht geladen werden");
    }
  };

  const handleDuplicate = async () => {
    if (!savedId) {
      toast.error("Bitte zuerst speichern");
      return;
    }
    try {
      const { data } = await api.post(`/recipes/${savedId}/duplicate`);
      toast.success(`"${data.name}" erstellt`);
      nav(`/rezepte/${data.id}`);
    } catch {
      toast.error("Duplizieren fehlgeschlagen");
    }
  };

  const handleRestore = async () => {
    if (!savedId || !restoreTarget) return;
    try {
      const { data } = await api.post(`/recipes/${savedId}/restore/${restoreTarget.id}`);
      // Update local state with restored data
      setName(data.name);
      setDescription(data.description || "");
      setGtin(data.gtin || "");
      setArtikelnummer(data.artikelnummer || "");
      setDeclareAsCooked(Boolean(data.declare_as_cooked));
      setPortions(data.portions || 1);
      setItems((data.items || []).map((it) => ({
        ingredient_id: it.ingredient_id,
        amount: it.amount != null ? it.amount : it.grams,
        unit: it.unit || "g",
      })));
      setCurrentVersion(data.version || 1);
      setComputed({
        total_grams: data.total_grams, totals: data.totals,
        per_100g: data.per_100g, per_portion: data.per_portion,
        allergens: data.allergens || [],
      });
      setAudit({
        created_by_name: data.created_by_name, created_at: data.created_at,
        updated_by_name: data.updated_by_name, updated_at: data.updated_at,
      });
      toast.success(`Version ${restoreTarget.version} wiederhergestellt`);
      setRestoreTarget(null);
      setVersionsOpen(false);
    } catch {
      toast.error("Wiederherstellen fehlgeschlagen");
    }
  };

  const exportFn = async (kind) => {
    if (!savedId) {
      toast.error("Bitte zuerst speichern");
      return;
    }
    try {
      const url = `${API_BASE}/recipes/${savedId}/export/${kind}`;
      const ext = kind === "pdf" ? "pdf" : kind === "label" ? "pdf" : "xlsx";
      const filename = kind === "label"
        ? `nutricalc_etiketten_${name.replace(/\s+/g, "_")}.pdf`
        : `nutricalc_${name.replace(/\s+/g, "_")}.${ext}`;
      await downloadFile(url, filename);
      toast.success(kind === "label" ? "Etiketten-PDF heruntergeladen" : `${kind.toUpperCase()} heruntergeladen`);
    } catch {
      toast.error("Export fehlgeschlagen");
    }
  };

  // ---------- Version Diff ----------
  const computeDiff = (snap) => {
    if (!snap) return null;
    const curItems = items.map((it) => ({
      ingredient_id: it.ingredient_id,
      amount: Number(it.amount) || 0,
      unit: it.unit || "g",
    }));
    const snapItems = (snap.items || []).map((it) => ({
      ingredient_id: it.ingredient_id,
      amount: it.amount != null ? Number(it.amount) : Number(it.grams || 0),
      unit: it.unit || "g",
    }));
    const curByIng = new Map(curItems.map((it) => [it.ingredient_id, it]));
    const snapByIng = new Map(snapItems.map((it) => [it.ingredient_id, it]));
    const added = []; const removed = []; const changed = [];
    curByIng.forEach((it, k) => {
      if (!snapByIng.has(k)) added.push(it);
      else {
        const o = snapByIng.get(k);
        if (o.amount !== it.amount || o.unit !== it.unit) {
          changed.push({ ingredient_id: k, from: o, to: it });
        }
      }
    });
    snapByIng.forEach((it, k) => { if (!curByIng.has(k)) removed.push(it); });
    const fieldDiff = [];
    if ((snap.name || "") !== (name || "")) fieldDiff.push({ field: "Name", from: snap.name, to: name });
    if ((snap.description || "") !== (description || "")) fieldDiff.push({ field: "Beschreibung", from: snap.description || "—", to: description || "—" });
    if (Number(snap.portions) !== Number(portions)) fieldDiff.push({ field: "Chargen", from: snap.portions, to: portions });
    return { added, removed, changed, fieldDiff };
  };

  const compareDiff = useMemo(() => computeDiff(compareTarget), [compareTarget, items, name, description, portions]);
  const ingName = (idRef) => ingById[idRef]?.name || "?";
  const fmtAmount = (it) =>
    `${formatNum(it.amount)} ${it.unit === "g" ? "g" : it.unit === "ml" ? "ml" : "Stk"}`;

  return (
    <div className="space-y-6" data-testid="recipe-builder-page">
      <div>
        <Link to="/rezepte" className="text-xs text-zinc-500 hover:text-zinc-800 inline-flex items-center gap-1">
          <ArrowLeft size={12} /> Zurück
        </Link>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mt-2">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
              {isEdit ? "Rezept bearbeiten" : "Neues Rezept"}
            </h1>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <p className="text-sm text-zinc-500">Zutaten + Mengen → Live-Nährwertberechnung.</p>
              {savedId && (
                <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-mono-num font-bold uppercase tracking-wider rounded-sm bg-zinc-100 text-zinc-700 border border-zinc-200"
                  data-testid="recipe-version-badge">
                  v{currentVersion}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" className="rounded-sm" disabled={!savedId}
              onClick={loadVersions} data-testid="builder-versions-button">
              <ClockCounterClockwise size={14} className="mr-1.5" /> Versionen
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm" disabled={!savedId}
              onClick={handleDuplicate} data-testid="builder-duplicate-button">
              <Copy size={14} className="mr-1.5" /> Duplizieren
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm" disabled={!savedId}
              onClick={() => exportFn("label")} data-testid="builder-export-label">
              <Tag size={14} className="mr-1.5" /> Etiketten
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm" disabled={!savedId}
              onClick={() => exportFn("pdf")} data-testid="builder-export-pdf">
              <FilePdf size={14} className="mr-1.5" /> PDF
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm" disabled={!savedId}
              onClick={() => exportFn("xlsx")} data-testid="builder-export-xlsx">
              <FileXls size={14} className="mr-1.5" /> Excel
            </Button>
            <Button onClick={save} disabled={busy} size="sm"
              className="bg-[#002FA7] hover:bg-[#00248a] rounded-sm" data-testid="save-recipe-button">
              <FloppyDisk size={14} className="mr-1.5" /> {busy ? "Speichern…" : "Speichern"}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left: recipe meta + ingredients list */}
        <div className="lg:col-span-3 space-y-6">
          <section className="border border-zinc-200 rounded-sm p-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-zinc-600">Rezeptname *</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="z. B. Limette Lachs Tagliatelle"
                  className="mt-1 rounded-sm" data-testid="recipe-name-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-600">Chargen</Label>
                <Input type="number" min="0.1" step="0.1" inputMode="decimal" value={portions}
                  onChange={(e) => setPortions(e.target.value)}
                  className="mt-1 rounded-sm font-mono-num" data-testid="recipe-portions-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-600">Beschreibung</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
                placeholder="Optionale Notizen…"
                className="mt-1 rounded-sm" rows={2} data-testid="recipe-description-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 pt-3 border-t border-zinc-100">
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-600">GTIN / EAN</Label>
                <Input
                  value={gtin}
                  onChange={(e) => setGtin(e.target.value)}
                  placeholder="z. B. 4012345678901"
                  className="mt-1 rounded-sm font-mono-num"
                  data-testid="recipe-gtin-input"
                  maxLength={20}
                />
                <p className="text-[11px] text-zinc-400 mt-1">Globale Artikelnummer (Barcode), optional</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-600">Artikelnummer (intern)</Label>
                <Input
                  value={artikelnummer}
                  onChange={(e) => setArtikelnummer(e.target.value)}
                  placeholder="z. B. ART-LIM-001"
                  className="mt-1 rounded-sm font-mono-num"
                  data-testid="recipe-artikelnummer-input"
                  maxLength={40}
                />
                <p className="text-[11px] text-zinc-400 mt-1">Eigene Artikel-/Produktnummer, optional</p>
              </div>
            </div>
            <div className="pt-3 border-t border-zinc-100">
              <label className="flex items-start gap-2 cursor-pointer" data-testid="declare-as-cooked-toggle">
                <Checkbox
                  checked={declareAsCooked}
                  onCheckedChange={(v) => setDeclareAsCooked(Boolean(v))}
                  className="mt-0.5"
                  data-testid="declare-as-cooked-checkbox"
                />
                <div className="text-sm">
                  <div className="font-medium text-zinc-900">Nährwerte je 100 g zubereitet ausweisen</div>
                  <p className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">
                    System rechnet automatisch das Endgewicht nach Garung (Garausbeute der Zutaten). Werte erscheinen auf Label / PDF / Excel als <b>{'„je 100 g zubereitet"'}</b>. Voraussetzung: Garausbeute ist beim Lebensmittel hinterlegt.
                  </p>
                </div>
              </label>
              {declareAsCooked && computed?.total_grams_cooked > 0 && (
                <div className="mt-2 text-[11px] font-mono-num text-zinc-600 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
                  Rohgewicht: <b>{formatKg(computed.total_grams)} kg</b> → zubereitet: <b>{formatKg(computed.total_grams_cooked)} kg</b>
                  {computed.total_grams > 0 && (
                    <> · Faktor {(computed.total_grams_cooked / computed.total_grams).toFixed(2)}×</>
                  )}
                </div>
              )}
            </div>
            {audit && (
              <div className="text-[11px] text-zinc-500 font-mono-num pt-3 border-t border-zinc-100">
                Erstellt von <b>{audit.created_by_name}</b> am {formatDateTime(audit.created_at)}
                {audit.updated_at && <> · zuletzt geändert von <b>{audit.updated_by_name}</b> am {formatDateTime(audit.updated_at)}</>}
              </div>
            )}
          </section>

          <section className="border border-zinc-200 rounded-sm">
            <div className="px-5 py-3 border-b border-zinc-200 flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-heading text-sm font-bold uppercase tracking-wider">Zutaten & Komponenten</h2>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={addRecipeComponent} className="rounded-sm"
                  data-testid="add-recipe-component">
                  <Plus size={12} className="mr-1" /> Rezept als Beilage
                </Button>
                <Button size="sm" onClick={addItem} className="rounded-sm bg-[#002FA7] hover:bg-[#00248a]"
                  data-testid="add-ingredient-row">
                  <Plus size={12} className="mr-1" /> Zutat hinzufügen
                </Button>
              </div>
            </div>
            {items.length === 0 ? (
              <div className="p-8 text-center text-sm text-zinc-500" data-testid="empty-recipe-items">
                Noch keine Zutaten hinzugefügt.
              </div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {items.map((it, idx) => {
                  const isRecipe = it.ref_type === "recipe";
                  const ing = !isRecipe ? ingById[it.ingredient_id] : null;
                  const subRec = isRecipe ? recipesList.find((r) => r.id === it.ingredient_id) : null;
                  const grams = itemGrams(it);
                  let kcal = 0;
                  if (isRecipe) {
                    kcal = (subRec?.per_100g?.energy_kcal || 0) * (grams / 100);
                  } else if (ing) {
                    kcal = (ing.nutrients?.energy_kcal || 0) * (grams / 100);
                  }
                  const canMl = !isRecipe && ing?.density_g_per_ml != null;
                  const canPiece = !isRecipe && ing?.piece_weight_g != null;
                  const showHint = it.unit && it.unit !== "g";
                  return (
                    <div key={it._uid || idx} className={`px-4 sm:px-5 py-3 grid grid-cols-12 gap-2 sm:gap-3 items-center ${isRecipe ? "bg-blue-50/40" : ""}`}
                      data-testid={`recipe-item-${idx}`}>
                      <div className="col-span-12 sm:col-span-5">
                        {isRecipe && (
                          <div className="text-[10px] uppercase tracking-widest text-blue-700 font-bold mb-1 flex items-center gap-1">
                            <BookOpenText size={10} weight="fill" /> Rezept als Komponente
                          </div>
                        )}
                        <Select value={it.ingredient_id}
                          onValueChange={(v) => {
                            if (isRecipe) {
                              updateItem(idx, { ingredient_id: v });
                              return;
                            }
                            const ni = ingById[v];
                            let newUnit = it.unit || "g";
                            // Trinkwasser-Erkennung: standardmäßig in Litern, sofern Dichte hinterlegt
                            if (isWaterIngredient(ni) && ni?.density_g_per_ml) {
                              newUnit = "l";
                            }
                            // ml/l sind jetzt immer erlaubt (Fallback Dichte 1.0 g/ml)
                            if (newUnit === "piece" && (ni?.piece_weight_g == null)) newUnit = "g";
                            updateItem(idx, { ingredient_id: v, unit: newUnit });
                          }}>
                          <SelectTrigger className="rounded-sm" data-testid={`item-${idx}-select`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {isRecipe
                              ? recipesList
                                  .filter((r) => r.id !== savedId)
                                  .map((r) => (
                                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                                  ))
                              : ingredients.map((i) => (
                                  <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>
                                ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <Input type="number" min="0" step="0.1" inputMode="decimal" value={it.amount ?? ""}
                          onChange={(e) => updateItem(idx, { amount: e.target.value })}
                          className="rounded-sm font-mono-num text-right"
                          data-testid={`item-${idx}-amount`} />
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <Select value={it.unit || "g"}
                          onValueChange={(v) => updateItem(idx, { unit: v })}>
                          <SelectTrigger className="rounded-sm" data-testid={`item-${idx}-unit`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="g">g</SelectItem>
                            <SelectItem value="kg">kg</SelectItem>
                            {!isRecipe && (
                              <SelectItem value="ml">
                                ml{!canMl ? " (≈ g)" : ""}
                              </SelectItem>
                            )}
                            {!isRecipe && (
                              <SelectItem value="l">
                                l{!canMl ? " (≈ kg)" : ""}
                              </SelectItem>
                            )}
                            {!isRecipe && (
                              <SelectItem value="piece" disabled={!canPiece}>
                                Stk{!canPiece ? " (kein Gewicht)" : ""}
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-3 sm:col-span-2 text-right font-mono-num text-xs sm:text-sm text-zinc-600">
                        <div>{formatNum(kcal)} kcal</div>
                        {showHint && (
                          <div className="text-[10px] text-zinc-400" data-testid={`item-${idx}-grams-hint`}>
                            = {formatKg(grams)} kg
                          </div>
                        )}
                      </div>
                      <div className="col-span-1 text-right">
                        <Button size="sm" variant="ghost"
                          className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                          onClick={() => removeItem(idx)}
                          data-testid={`item-${idx}-remove`}>
                          <Trash size={14} />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {items.length > 0 && (
              <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50 flex justify-between text-sm">
                <span className="text-zinc-600">Gesamtgewicht</span>
                <span className="font-mono-num font-bold">{formatKg(computed.total_grams)} kg</span>
              </div>
            )}
            {ingredients.length === 0 && (
              <div className="px-5 py-3 text-xs text-zinc-500 border-t border-zinc-200">
                Tipp: Lege zuerst <Link to="/zutaten/neu" className="text-[#002FA7] underline">Zutaten</Link> an.
              </div>
            )}
          </section>
        </div>

        {/* Right: live nutrition panel */}
        <aside className="lg:col-span-2">
          <div className="border border-zinc-200 rounded-sm lg:sticky lg:top-4">
            <div className="px-5 py-3 border-b border-zinc-200 bg-[#002FA7] text-white">
              <h2 className="font-heading text-sm font-bold uppercase tracking-wider">Nährwerte (Live)</h2>
              <p className="text-[11px] opacity-80 mt-0.5">Gemäß EU VO 1169/2011</p>
            </div>

            {computed.nutri_score && computed.total_grams > 0 && (
              <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between" data-testid="nutri-score-block">
                <div className="text-[10px] uppercase tracking-widest text-zinc-600 font-bold">
                  Nutri-Score
                </div>
                <NutriScoreBadge score={computed.nutri_score} size="md" />
              </div>
            )}

            {(Number(computed.alcohol_g_per_100g || 0) > 0 || computed.contains_alcohol) && (
              <div className="px-5 py-2 bg-amber-50 border-b border-amber-200 text-xs" data-testid="alcohol-summary">
                <div className="flex justify-between items-center">
                  <span className="font-bold uppercase tracking-widest text-[10px] text-amber-900">
                    Alkohol-Hinweis
                  </span>
                  <span className="text-amber-900 font-medium">Enthält Alkohol</span>
                </div>
                <div className="mt-1 text-[11px] text-amber-800">
                  Verwendet beim Kochen — Etikett-Aufdruck: <span className="font-bold">{'„Enthält Alkohol"'}</span> (Alkohol kann beim Garen entweichen, daher keine Vol-%-Angabe auf dem Endprodukt).
                </div>
              </div>
            )}

            <div className="px-5 py-3 bg-amber-50 border-b border-amber-200" data-testid="allergen-summary">
              <div className="text-[10px] uppercase tracking-widest text-amber-800 font-bold mb-1.5">
                Allergene
              </div>
              {(computed.allergens || []).length === 0 ? (
                <div className="text-xs text-zinc-500">
                  Keine kennzeichnungspflichtigen Allergene aus den verwendeten Zutaten.
                </div>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {(computed.allergens || []).map((k) => (
                    <span key={k}
                      className="inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-sm bg-amber-200 text-amber-900 border border-amber-300"
                      data-testid={`allergen-badge-${k}`}>
                      {allergenLabel[k] || k}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="max-h-[60vh] overflow-y-auto">
              {NUTRIENT_GROUPS.map((grp) => (
                <div key={grp.label}>
                  <div className="px-5 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-widest text-zinc-600 font-bold">
                    {grp.label}
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-zinc-400">
                        <th className="px-5 py-1.5 text-left font-medium">Nährstoff</th>
                        <th className="px-2 py-1.5 text-right font-medium">Gesamt</th>
                        <th className="px-2 py-1.5 text-right font-medium">{declareAsCooked ? "/100g zub." : "/100g"}</th>
                        <th className="px-2 py-1.5 text-right font-medium">/Charge</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grp.keys.map((k) => {
                        const m = metaByKey[k];
                        if (!m) return null;
                        return (
                          <tr key={k} className="border-t border-zinc-100">
                            <td className="px-5 py-1.5">
                              <span>{m.label}</span>
                              <span className="text-zinc-400 ml-1">({m.unit})</span>
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono-num">{formatNum(computed.totals?.[k] || 0)}</td>
                            <td className="px-2 py-1.5 text-right font-mono-num text-zinc-600">{formatNum(computed.per_100g?.[k] || 0)}</td>
                            <td className="px-2 py-1.5 text-right font-mono-num text-zinc-600">{formatNum(computed.per_portion?.[k] || 0)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {/* Versions dialog */}
      <Dialog open={versionsOpen} onOpenChange={setVersionsOpen}>
        <DialogContent className="max-w-2xl" data-testid="versions-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">Versionsverlauf</DialogTitle>
            <DialogDescription>
              Bei jeder Änderung wird automatisch ein Snapshot der vorherigen Version gespeichert.
              Aktuelle Version: <b className="font-mono-num">v{currentVersion}</b>
            </DialogDescription>
          </DialogHeader>
          {versions.length === 0 ? (
            <div className="py-8 text-center text-sm text-zinc-500" data-testid="versions-empty">
              Keine früheren Versionen vorhanden — dieses Rezept wurde noch nicht geändert.
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto -mx-6 border-y border-zinc-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500 bg-zinc-50">
                    <th className="px-6 py-2 font-medium">Version</th>
                    <th className="px-3 py-2 font-medium">Snapshot am</th>
                    <th className="px-3 py-2 font-medium">Von</th>
                    <th className="px-3 py-2 font-medium">Grund</th>
                    <th className="px-3 py-2 font-medium text-right">Zutaten</th>
                    <th className="px-6 py-2 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map((v) => (
                    <tr key={v.id} className="border-t border-zinc-100"
                      data-testid={`version-row-${v.version}`}>
                      <td className="px-6 py-2 font-mono-num font-bold">v{v.version}</td>
                      <td className="px-3 py-2 text-zinc-600 font-mono-num text-xs">
                        {formatDateTime(v.snapshot_at)}
                      </td>
                      <td className="px-3 py-2 text-zinc-600">{v.snapshot_by_name}</td>
                      <td className="px-3 py-2 text-zinc-500 text-xs">
                        {v.reason === "pre-restore"
                          ? "vor Wiederherstellung"
                          : v.reason === "update"
                          ? "Update"
                          : v.reason}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-num">{(v.items || []).length}</td>
                      <td className="px-6 py-2 text-right">
                        <div className="flex justify-end gap-1.5">
                          <Button size="sm" variant="outline" className="rounded-sm"
                            onClick={() => { setCompareTarget(v); setVersionsOpen(false); }}
                            data-testid={`compare-version-${v.version}`}>
                            Vergleichen
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-sm"
                            onClick={() => setRestoreTarget(v)}
                            data-testid={`restore-version-${v.version}`}>
                            <ArrowCounterClockwise size={12} className="mr-1.5" />
                            Wiederherstellen
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setVersionsOpen(false)}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change reason dialog (only for edits) */}
      <Dialog open={reasonOpen} onOpenChange={(o) => { if (!o) setReasonOpen(false); }}>
        <DialogContent className="max-w-md" data-testid="change-reason-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tight">Änderungsgrund</DialogTitle>
            <DialogDescription>
              Optional, aber empfohlen: Beschreibe kurz, warum du dieses Rezept änderst. Der Grund
              wird im Versionsverlauf gespeichert und hilft bei Audits.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={changeReason}
            onChange={(e) => setChangeReason(e.target.value)}
            placeholder="z. B. Salz reduziert nach Verkostung am 12.02."
            rows={3}
            className="rounded-sm"
            data-testid="change-reason-input"
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setReasonOpen(false)}
              data-testid="change-reason-cancel">
              Abbrechen
            </Button>
            <Button className="bg-[#002FA7] hover:bg-[#00248a] rounded-sm"
              onClick={() => doSave(changeReason.trim())} disabled={busy}
              data-testid="change-reason-submit">
              {busy ? "Speichern…" : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version compare dialog */}
      <Dialog open={!!compareTarget} onOpenChange={(o) => !o && setCompareTarget(null)}>
        <DialogContent className="max-w-3xl" data-testid="compare-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">
              Vergleich: v{compareTarget?.version} ↔ aktuell (v{currentVersion})
            </DialogTitle>
            <DialogDescription>
              Linke Spalte = gespeicherte Version (Snapshot vom {compareTarget && formatDateTime(compareTarget.snapshot_at)}).
              Rechte Spalte = aktueller, ungespeicherter Stand im Builder.
            </DialogDescription>
          </DialogHeader>

          {compareDiff && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto -mx-6 px-6">
              {compareDiff.fieldDiff.length === 0 &&
               compareDiff.added.length === 0 &&
               compareDiff.removed.length === 0 &&
               compareDiff.changed.length === 0 ? (
                <div className="py-8 text-center text-sm text-zinc-500" data-testid="compare-empty">
                  Keine Unterschiede gefunden — der aktuelle Builder-Stand entspricht dieser Version.
                </div>
              ) : (
                <>
                  {compareDiff.fieldDiff.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">
                        Geänderte Felder
                      </div>
                      <table className="w-full text-sm border border-zinc-200 rounded-sm">
                        <thead>
                          <tr className="bg-zinc-50 text-left text-[11px] uppercase tracking-wider text-zinc-500">
                            <th className="px-3 py-2 font-medium">Feld</th>
                            <th className="px-3 py-2 font-medium">v{compareTarget?.version}</th>
                            <th className="px-3 py-2 font-medium">aktuell</th>
                          </tr>
                        </thead>
                        <tbody>
                          {compareDiff.fieldDiff.map((d) => (
                            <tr key={d.field} className="border-t border-zinc-100">
                              <td className="px-3 py-2 font-medium">{d.field}</td>
                              <td className="px-3 py-2 font-mono-num text-red-700 bg-red-50">{String(d.from)}</td>
                              <td className="px-3 py-2 font-mono-num text-green-700 bg-green-50">{String(d.to)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {compareDiff.added.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-green-700 font-bold mb-2">
                        Hinzugefügt ({compareDiff.added.length})
                      </div>
                      <ul className="text-sm border border-green-200 rounded-sm bg-green-50/40 divide-y divide-green-100">
                        {compareDiff.added.map((it) => (
                          <li key={it.ingredient_id} className="px-3 py-2 flex justify-between"
                            data-testid={`diff-added-${it.ingredient_id}`}>
                            <span>{ingName(it.ingredient_id)}</span>
                            <span className="font-mono-num text-green-800">+ {fmtAmount(it)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {compareDiff.removed.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-red-700 font-bold mb-2">
                        Entfernt ({compareDiff.removed.length})
                      </div>
                      <ul className="text-sm border border-red-200 rounded-sm bg-red-50/40 divide-y divide-red-100">
                        {compareDiff.removed.map((it) => (
                          <li key={it.ingredient_id} className="px-3 py-2 flex justify-between"
                            data-testid={`diff-removed-${it.ingredient_id}`}>
                            <span>{ingName(it.ingredient_id)}</span>
                            <span className="font-mono-num text-red-800">− {fmtAmount(it)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {compareDiff.changed.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold mb-2">
                        Menge geändert ({compareDiff.changed.length})
                      </div>
                      <table className="w-full text-sm border border-amber-200 rounded-sm">
                        <thead>
                          <tr className="bg-amber-50 text-left text-[11px] uppercase tracking-wider text-amber-800">
                            <th className="px-3 py-2 font-medium">Zutat</th>
                            <th className="px-3 py-2 font-medium">v{compareTarget?.version}</th>
                            <th className="px-3 py-2 font-medium">aktuell</th>
                          </tr>
                        </thead>
                        <tbody>
                          {compareDiff.changed.map((c) => (
                            <tr key={c.ingredient_id} className="border-t border-amber-100"
                              data-testid={`diff-changed-${c.ingredient_id}`}>
                              <td className="px-3 py-2">{ingName(c.ingredient_id)}</td>
                              <td className="px-3 py-2 font-mono-num text-red-700">{fmtAmount(c.from)}</td>
                              <td className="px-3 py-2 font-mono-num text-green-700">{fmtAmount(c.to)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => { setCompareTarget(null); setVersionsOpen(true); }}>
              Zurück zu Versionen
            </Button>
            <Button className="bg-[#002FA7] hover:bg-[#00248a] rounded-sm"
              onClick={() => { setRestoreTarget(compareTarget); }}>
              Diese Version wiederherstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore confirm */}
      <AlertDialog open={!!restoreTarget} onOpenChange={(o) => !o && setRestoreTarget(null)}>
        <AlertDialogContent data-testid="restore-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Version v{restoreTarget?.version} wiederherstellen?</AlertDialogTitle>
            <AlertDialogDescription>
              Der aktuelle Stand wird vorher automatisch als Snapshot gesichert. Du kannst danach
              jederzeit zur jetzigen Version zurückkehren.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestore}
              className="bg-[#002FA7] hover:bg-[#00248a]"
              data-testid="confirm-restore">
              Wiederherstellen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
